import React from "react";

type V7ProductPhoneProps = {
  activeView: number;
};

const MODEL_ALLOCATIONS = [
  { name: "Alpha Core", weight: 0.3 },
  { name: "SPX Select", weight: 0.25 },
  { name: "Yield Plus", weight: 0.2 },
  { name: "Tech Leaders", weight: 0.15 },
  { name: "Dividend Growth", weight: 0.1 },
];

const MAX_MODEL_WEIGHT = Math.max(...MODEL_ALLOCATIONS.map(({ weight }) => weight));

const PhoneChart: React.FC = () => (
  <section className="product-phone-chart" aria-label="Illustrative portfolio value chart">
    <div className="product-phone-chart-head">
      <p>Portfolio value</p>
      <span>All time</span>
    </div>
    <svg viewBox="0 0 300 116" role="img" aria-label="Illustrative portfolio value rising over time">
      <defs>
        <linearGradient id="phone-chart-fill" x1="0" y1="0" x2="0" y2="1">
          <stop className="product-phone-chart-stop" offset="0" stopOpacity=".22" />
          <stop className="product-phone-chart-stop" offset="1" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        className="product-phone-chart-area"
        d="M0 94C18 88 28 91 42 82s27-4 40-13 25 2 41-11 28-21 43-15 23 2 40-10 27-4 43-14 29-5 51-17V116H0Z"
      />
      <path
        className="product-phone-chart-line"
        d="M0 94C18 88 28 91 42 82s27-4 40-13 25 2 41-11 28-21 43-15 23 2 40-10 27-4 43-14 29-5 51-17"
      />
    </svg>
    <div className="product-phone-periods" aria-hidden="true">
      <span>1D</span><span>1W</span><span>1M</span><span>3M</span><span>1Y</span><span className="is-active">All</span>
    </div>
  </section>
);

const V7ProductPhone: React.FC<V7ProductPhoneProps> = ({ activeView }) => (
  <div className="product-phone-frame" aria-label="Illustrative Plutus mobile portfolio interface">
    <div className="product-phone-screen">
      <div className="product-phone-status" aria-hidden="true">
        <span>9:41</span>
        <span className="product-phone-speaker" />
        <span className="product-phone-signal"><i /><i /><i /></span>
      </div>

      <div className="product-phone-app">
        <div className="product-phone-header">
          <div className="product-phone-brand"><img src="/favicon.png" alt="" /><span>Plutus</span></div>
          <span className="product-phone-avatar">SM</span>
        </div>

        <div className="product-phone-account">
          <p>Individual portfolio</p>
          <span>Brokerage connected</span>
        </div>
        <p className="product-phone-value">$847,231</p>
        <p className="product-phone-meta"><strong>On target</strong><span>Last checked today</span></p>
        <PhoneChart />

        <div className="product-phone-views">
          <section
            className={`product-phone-view${activeView === 0 ? " is-active" : ""}`}
            aria-hidden={activeView !== 0}
            aria-label="Tune portfolio preferences"
          >
            <div className="product-view-head"><h4>Portfolio preferences</h4></div>
            <div className="product-preference-summary">
              <div className="product-preference-line"><span>Objective</span><strong>Maximum growth</strong></div>
              <div className="product-preference-line"><span>Risk approach</span><strong>Keep risk balanced</strong></div>
              <div className="product-preference-line"><span>Time horizon</span><strong>7+ years</strong></div>
              <div className="product-preference-actions">
                <div className="product-risk-score-compact"><span>Overall risk score</span><strong>6 / 10</strong></div>
                <button className="product-preference-button" type="button" tabIndex={-1}>Update</button>
              </div>
            </div>
          </section>

          <section
            className={`product-phone-view${activeView === 1 ? " is-active" : ""}`}
            aria-hidden={activeView !== 1}
            aria-label="Portfolio allocation"
          >
            <div className="product-phone-allocation-head"><h4>Model allocation</h4><span>5 models</span></div>
            <div className="product-phone-models" aria-label="Illustrative model weights">
              {MODEL_ALLOCATIONS.map(({ name, weight }) => (
                <div className="product-phone-model" key={name}>
                  <p>{name}</p>
                  <span className="product-phone-weight">
                    <i style={{ width: `${(weight / MAX_MODEL_WEIGHT) * 100}%` }} />
                  </span>
                  <strong>{weight * 100}%</strong>
                </div>
              ))}
            </div>
          </section>

          <section
            className={`product-phone-view${activeView === 2 ? " is-active" : ""}`}
            aria-hidden={activeView !== 2}
            aria-label="Recent portfolio activity"
          >
            <div className="product-view-head"><h4>Recent changes</h4></div>
            <div className="product-activity-timeline">
              <article className="product-activity-item">
                <span className="product-activity-dot" aria-hidden="true" />
                <p className="product-activity-time">Today / 9:42 AM</p>
                <h5>Portfolio rebalance completed</h5>
                <p>Trades brought all five models back within their target ranges.</p>
              </article>
              <article className="product-activity-item">
                <span className="product-activity-dot" aria-hidden="true" />
                <p className="product-activity-time">Yesterday</p>
                <h5>Tech Leaders model updated</h5>
                <p>The provider reduced one position and raised its cash target. Your allocation was adjusted.</p>
              </article>
              <article className="product-activity-item">
                <span className="product-activity-dot" aria-hidden="true" />
                <p className="product-activity-time">July 24</p>
                <h5>Yield Plus weight increased</h5>
                <p>Target weight moved from 18% to 20% after the portfolio risk review.</p>
              </article>
            </div>
          </section>
        </div>
      </div>

      <nav className="product-phone-nav" aria-label="Illustrative mobile navigation">
        <span className="is-active"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 18V9m7 9V5m7 13v-7" /></svg>Dashboard</span>
        <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h4l2.5-6 5 12 2.5-6H21" /></svg>Activity</span>
        <span><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="7" /><path d="m16.5 7.5-3 6-6 3 3-6Z" /></svg>Discover</span>
      </nav>
    </div>
  </div>
);

export default V7ProductPhone;
